import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { ContentEditable } from '@lexical/react/LexicalContentEditable';
import LexicalErrorBoundary from '@lexical/react/LexicalErrorBoundary';
import { RichTextPlugin } from '@lexical/react/LexicalRichTextPlugin';
import {
  $createParagraphNode,
  $getRoot,
  $insertNodes,
  BLUR_COMMAND,
  COMMAND_PRIORITY_NORMAL,
  FOCUS_COMMAND,
} from 'lexical';
import PropTypes from 'prop-types';
import { useEffect, useRef } from 'react';
import { twJoin } from 'tailwind-merge';

import useToast from 'hooks/useToast';

import { isImageMimeTypeValid, isImageSizeValid } from 'lib/image';
import { toAbsoluteLink } from 'lib/links';
import { isYoutubeUrlValid } from 'lib/string';
import { $createWhitespace, $generateNodesFromStr } from 'lib/textEditor';

import { postUploadImage } from 'services/image';

import Answering from './Answering';
import { $createMentionNode } from './Nodes/MentionNode';
import { $createSkeletonNode } from './Nodes/SkeletonNode';
import TextNode from './Nodes/TextNode';
import { $createVideoNode } from './Nodes/VideoNode';
import Placeholder from './Placeholder';
import Plugins from './Plugins';
import { INSERT_IMAGE_COMMAND } from './Plugins/ImagePlugin';

import IMAGE from 'constants/image';

const KB_TO_MB_DIVIDER = 1_000_000;

const isUrl = (str) => str.startsWith('http');

const RichText = ({
  answering,
  className,
  disabled = false,
  error = false,
  hasToolbar = false,
  initialValue,
  placeholder,
  onBlur,
  onChange,
  onFocus,
  onSend,
}) => {
  const containerRef = useRef(null);
  const { showToast } = useToast();
  const [editor] = useLexicalComposerContext();

  useEffect(() => {
    const cleanupBlurCommand = editor.registerCommand(
      BLUR_COMMAND,
      () => {
        if (disabled) {
          return;
        }

        if (onBlur) {
          onBlur();
        }

        return true;
      },
      COMMAND_PRIORITY_NORMAL
    );

    const cleanupFocusCommand = editor.registerCommand(
      FOCUS_COMMAND,
      () => {
        if (disabled) {
          return;
        }

        if (onFocus) {
          onFocus();
        }

        return true;
      },
      COMMAND_PRIORITY_NORMAL
    );

    return () => {
      cleanupBlurCommand();
      cleanupFocusCommand();
    };
  }, [editor]);

  useEffect(() => {
    const insertPastedImageToTextEditor = async (event) => {
      const file = event.clipboardData.files[0];

      if (!file || !isImageMimeTypeValid(file)) {
        return;
      }

      if (!isImageSizeValid(file)) {
        const maxImageSizeAllowedInMegabytes =
          IMAGE.MAX_IMAGE_SIZE_KB / KB_TO_MB_DIVIDER;

        showToast({
          type: 'danger',
          text: `Ops! Esta imagem excede o limite de ${maxImageSizeAllowedInMegabytes}MB`,
        });
        return;
      }

      editor.update(async () => {
        const skeletonNode = $createSkeletonNode();

        $insertNodes([skeletonNode]);

        try {
          const { path } = await postUploadImage({ file });

          editor.dispatchCommand(INSERT_IMAGE_COMMAND, {
            src: toAbsoluteLink(path),
            fallback: skeletonNode,
          });
        } catch {
          skeletonNode.remove();
          showToast({
            type: 'danger',
            text: placeholder('toasts.errors.unknown'),
          });
        }
      });
    };

    containerRef.current.addEventListener(
      'paste',
      insertPastedImageToTextEditor
    );

    return () => {
      containerRef.current?.removeEventListener(
        'paste',
        insertPastedImageToTextEditor
      );
    };
  }, [editor]);

  useEffect(() => {
    const unregister = editor.registerNodeTransform(TextNode, (node) => {
      if (isUrl(node.__text) && isYoutubeUrlValid(node.__text)) {
        node.replace($createVideoNode({ src: node.__text }));
      }
    });

    return () => {
      unregister();
    };
  }, [editor]);

  useEffect(() => {
    if (initialValue || answering) {
      editor.update(() => {
        const root = $getRoot();
        const paragraph = $createParagraphNode();

        if (answering) {
          const mentionNode = $createMentionNode(answering);
          const whitespaceNode = $createWhitespace();

          paragraph.append(mentionNode, whitespaceNode);
          paragraph.append();
          whitespaceNode.select();
        }

        if (initialValue) {
          const nodes = $generateNodesFromStr(initialValue, editor);

          nodes.forEach((node) => paragraph.append(node));
        }

        root.getFirstChild().replace(paragraph);

        return true;
      });
    }
  }, [initialValue, editor]);

  return (
    <div
      ref={containerRef}
      className={twJoin(
        'peer relative flex items-center rounded-3 border border-b-0 border-solid px-4 py-2 pt-2 focus-within:border-primary-300 dark:focus-within:border-primary-300',
        disabled
          ? 'cursor-not-allowed border-neutral-low-200 bg-neutral-high-200 dark:border-neutral-high-500 dark:bg-neutral-low-400'
          : 'border-neutral-low-300 bg-neutral-high-100 dark:border-neutral-high-100 dark:bg-neutral-low-500',
        hasToolbar && 'rounded-b-none',
        className
      )}
    >
      <RichTextPlugin
        ErrorBoundary={LexicalErrorBoundary}
        contentEditable={
          <ContentEditable
            className={twJoin(
              'max-h-56 min-h-20 w-full overflow-y-auto whitespace-pre-wrap font-sans text-base font-regular text-neutral-low-200 outline-none transition-all ease-in-out dark:text-neutral-high-100 md:max-h-80',
              answering && 'min-h-20 pt-8'
            )}
            disabled={disabled}
          />
        }
        placeholder={
          <Placeholder
            answering={!!answering}
            className={twJoin(
              'absolute left-4 leading-none',
              answering ? 'top-10' : 'top-4',
              onSend && 'max-w-[90%]'
            )}
            disabled={disabled}
            error={error}
          >
            {placeholder}
          </Placeholder>
        }
      />
      {answering && <Answering>{answering}</Answering>}
      <Plugins onChange={onChange} />
    </div>
  );
};

RichText.propTypes = {
  answering: PropTypes.string,
  className: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
  disabled: PropTypes.bool,
  error: PropTypes.bool,
  hasToolbar: PropTypes.bool,
  initialValue: PropTypes.string,
  placeholder: PropTypes.string.isRequired,
  onBlur: PropTypes.func,
  onChange: PropTypes.func,
  onFocus: PropTypes.func,
  onSend: PropTypes.func,
};

export default RichText;
